/**
 * The choice banner's options that need working out (main.ts `showPrompt` draws them): which Strike a point-blank
 * click makes, and whom a potion goes to. Pure, no DOM (it imports only types), so the tests read them straight off a
 * PlayerView.
 */
import type { CreatureView, PlayerView, StrikeView } from '../shared/view.ts';

/** One option of the Strike banner: a Strike (`strikeKey`), or a combination weapon to switch to its blade (`switchUid`). */
export interface StrikePick {
  label: string;
  title?: string;
  /** Why it can't be picked now (a gun that isn't loaded). */
  disabled?: string;
  strikeKey?: string;
  switchUid?: string;
}

/** One option of the Use banner: yourself (no `creatureId`), or a creature next to you to feed it to. */
export interface FeedPick {
  label: string;
  creatureId?: string;
}

const signed = (n: number) => (n >= 0 ? `+${n}` : `–${Math.abs(n)}`);

const axisGap = (a0: number, as: number, b0: number, bs: number) => Math.max(0, b0 - (a0 + as - 1), a0 - (b0 + bs - 1));

/** Squares between two creatures' spaces (sizes counted), PF2e diagonals (5-10-5): the server's `distance`. */
export function spaceGap(a: CreatureView, b: CreatureView): number {
  const dx = axisGap(a.x, a.size ?? 1, b.x, b.size ?? 1);
  const dy = axisGap(a.y, a.size ?? 1, b.y, b.size ?? 1);
  return Math.max(dx, dy) + Math.floor(Math.min(dx, dy) / 2);
}

const REACTIVE = "A shot within a foe's reach can draw its Reactive Strike";

/**
 * The banner's options when your lead weapon is a ranged one that would Strike `target` within reach of one of your
 * melee Strikes; null when there's nothing to ask (the server's default Strike goes). The lead weapon is the one in
 * your main hand, or, when your main hand makes no Strike (empty after a Swap Hands or a Disarm), the one in your off
 * hand: a rapier in the main hand and a crossbow in the off hand still just swings. In a fixed order, so the keys never
 * move: the shots (main hand first; a gun that can't fire greyed with why), the melee Strikes that reach (thrown ones
 * stay on the sheet), then a Switch to the blade for each combination weapon in its ranged usage. It asks only when two
 * of those Strikes could be made now.
 */
export function pointBlankPicks(view: PlayerView, target: CreatureView): StrikePick[] | null {
  const you = view.dungeon?.creatures.find((c) => c.isYou);
  const strikes = view.sheet?.strikes ?? [];
  if (!you) return null;
  const main = strikes.find((k) => k.hand === 'main' || k.hand === 'both') ?? strikes.find((k) => k.hand === 'off');
  if (!main?.ranged || main.thrown) return null;
  const gap = spaceGap(you, target);
  const shots = strikes.filter((k) => k.ranged && !k.thrown && (!k.followUpOf || k.followUpOf === target.id));
  const melee = strikes.filter((k) => !k.ranged && gap <= (k.reach ?? 1));
  if (!melee.length) return null;
  if ([...shots, ...melee].filter((k) => !k.unready).length < 2) return null;
  const pick = (k: StrikeView, title?: string): StrikePick => ({
    label: `${k.name} ${signed(k.attack)}`, strikeKey: k.key, ...(k.unready ? { disabled: k.unready } : title ? { title } : {}),
  });
  const switching = view.actions.some((a) => a.id === 'switch-mode' && a.enabled)
    ? (view.sheet?.items ?? []).filter((i) => i.location === 'hand' && i.mode === 'ranged').map((i): StrikePick => ({
      label: `Switch ${i.name} to melee ◆`, switchUid: i.uid, title: 'Switch it to its melee usage (an Interact action); this makes no Strike',
    }))
    : [];
  return [...shots.map((k) => pick(k, REACTIVE)), ...melee.map((k) => pick(k)), ...switching];
}

/** How a creature you could feed is doing: dying, unconscious, or its Hit Points (its health when they're not shown). */
function feedLabel(c: CreatureView): string {
  const dying = c.status.find((s) => s.id === 'dying');
  if (dying) return `${c.name} (dying ${dying.value ?? 1})`;
  if (c.health === 'down') return `${c.name} (unconscious)`;
  if (c.hp !== undefined && c.maxHp) return `${c.name} (${c.hp}/${c.maxHp} HP)`;
  return `${c.name} (${c.health})`;
}

/**
 * The Use banner's options: yourself first, then each creature next to you that you could feed (Use Item's
 * `recipients`, neediest first; for a drink that hurts, item `uid`'s own `feedTo`: allies that are up), with how
 * they're doing. Null: there's no one else, so you just drink it.
 */
export function feedPicks(view: PlayerView, uid?: string): FeedPick[] | null {
  const own = uid ? view.sheet?.items.find((i) => i.uid === uid)?.feedTo : undefined;
  const ids = own ?? view.actions.find((a) => a.id === 'use-item')?.recipients ?? [];
  const creatures = view.dungeon?.creatures ?? [];
  const others = ids.flatMap((id) => {
    const c = creatures.find((x) => x.id === id);
    return c ? [{ label: feedLabel(c), creatureId: c.id }] : [];
  });
  if (!others.length) return null;
  const you = creatures.find((c) => c.isYou);
  return [{ label: you?.hp !== undefined && you.maxHp ? `Yourself (${you.hp}/${you.maxHp} HP)` : 'Yourself' }, ...others];
}
